// covered-by: frontends/carrie/src/routes/ask-puntt/utils/__tests__/participationApprovalTasks.test.ts
import { type ParticipationTask } from "@mg/schemas/src/christo/participationTask";

import { type ProjectReviewItem } from "../../../services/askPunttProjectFeed.types";
import {
  type ApprovalRequestAssetVisual,
  parseAssetLabel,
} from "../components/ApprovalRequestForm";

function taskUpdatedAtMs(task: ParticipationTask): number {
  const raw = task.updatedAt ?? task.openedAt;
  if (raw == null) return 0;
  return raw instanceof Date ? raw.getTime() : Date.parse(String(raw)) || 0;
}

/** Prefer ticket match first — stale tasks may carry a wrong fileId but valid ticketId. */
export function findReviewForParticipationTask(
  task: ParticipationTask,
  reviews: ProjectReviewItem[],
): ProjectReviewItem | null {
  if (reviews.length === 0) return null;

  if (task.ticketId != null) {
    const byTicket = reviews.find((r) => r.ticketId === task.ticketId);
    if (byTicket) return byTicket;
  }

  if (task.fileId) {
    const byFile = reviews.find((r) => r.fileId === task.fileId);
    if (byFile) return byFile;
  }

  return null;
}

/** Stable group key for collapsing tasks that weigh in on the same project review. */
export function reviewIdentityKey(review: ProjectReviewItem): string | null {
  if (review.ticketId) return `ticket:${review.ticketId}`;
  if (review.fileId) return `file:${review.fileId}`;
  if (review.id) return `review:${review.id}`;
  return null;
}

/** Review identity for a task when reviews are loaded (null when unmatched). */
export function resolvedReviewIdentityKey(
  task: ParticipationTask,
  reviews: ProjectReviewItem[],
): string | null {
  const review = findReviewForParticipationTask(task, reviews);
  return review ? reviewIdentityKey(review) : null;
}

function isOrphanStaleTask(
  task: ParticipationTask,
  reviews: ProjectReviewItem[],
): boolean {
  // ticketId alone is not enough — without a label or matching review the
  // composer would fall back to the "Review asset" placeholder.
  if (task.assetLabel?.trim()) return false;
  if (findReviewForParticipationTask(task, reviews) != null) return false;
  return true;
}

/**
 * Project-feed composer: rows that would only render the generic
 * "Review asset" placeholder. A ticketId alone is not enough — without a
 * matching loaded review or an assetLabel, the card has nothing real to show.
 * IDLE projects (empty reviews) must render zero cards for these rows, not
 * two identical placeholders.
 */
export function isUnusableProjectFeedTask(
  task: ParticipationTask,
  reviews: ProjectReviewItem[],
): boolean {
  if (!task.versionId?.trim() || !task.fileId?.trim()) return true;
  if (findReviewForParticipationTask(task, reviews) != null) return false;
  if (task.assetLabel?.trim()) return false;
  return true;
}

/** Belt-and-suspenders pipeline for the shared project feed composer. */
export function filterProjectFeedParticipationTasks(
  tasks: ParticipationTask[],
  projectId: string,
  reviews: ProjectReviewItem[] = [],
): ParticipationTask[] {
  const scoped = tasks.filter((task) => task.projectId === projectId);
  const deduped = dedupeOpenParticipationTasks(scoped, reviews);
  return deduped.filter((task) => !isUnusableProjectFeedTask(task, reviews));
}

/**
 * Drop legacy open rows with null ticketId / no label / no review match.
 * Always — including when reviews are empty (IDLE). The previous early-return
 * on `reviews.length === 0` left orphan rows as "Review asset" placeholders
 * on every empty project feed.
 */
function dropOrphanStaleTasks(
  tasks: ParticipationTask[],
  reviews: ProjectReviewItem[],
): ParticipationTask[] {
  return tasks.filter((task) => !isOrphanStaleTask(task, reviews));
}

function taskPriority(
  task: ParticipationTask,
  review: ProjectReviewItem | null,
): number {
  let score = taskUpdatedAtMs(task) / 1_000_000_000_000;
  if (review) score += 1_000;
  if (task.assetLabel?.trim()) score += 100;
  if (task.ticketId) score += 50;
  if (task.fileId) score += 10;
  return score;
}

function pickPreferredTask(
  current: ParticipationTask,
  candidate: ParticipationTask,
  reviews: ProjectReviewItem[],
): ParticipationTask {
  const currentScore = taskPriority(
    current,
    findReviewForParticipationTask(current, reviews),
  );
  const candidateScore = taskPriority(
    candidate,
    findReviewForParticipationTask(candidate, reviews),
  );
  return candidateScore > currentScore ? candidate : current;
}

/**
 * Collapse duplicate open tasks for the same review/version so the shared feed
 * shows one card. API rows can linger (stale versionId + fresh row for the same
 * ticket) after re-uploads or re-suggestions.
 */
export function dedupeOpenParticipationTasks(
  tasks: ParticipationTask[],
  reviews: ProjectReviewItem[] = [],
): ParticipationTask[] {
  // Drop unusable orphans first — including the length-1 case (previously an
  // early return left a single "Review asset" placeholder intact).
  let deduped = dropOrphanStaleTasks(tasks, reviews);
  if (deduped.length <= 1) return deduped;

  const byVersionId = new Map<string, ParticipationTask>();
  for (const task of deduped) {
    const existing = byVersionId.get(task.versionId);
    byVersionId.set(
      task.versionId,
      existing ? pickPreferredTask(existing, task, reviews) : task,
    );
  }

  deduped = Array.from(byVersionId.values());

  const collapseByKey = (
    keyFor: (task: ParticipationTask) => string | null,
  ) => {
    const groups = new Map<string, ParticipationTask>();
    for (const task of deduped) {
      const key = keyFor(task);
      if (!key) continue;
      const existing = groups.get(key);
      groups.set(
        key,
        existing ? pickPreferredTask(existing, task, reviews) : task,
      );
    }
    if (groups.size === 0) return;
    const absorbed = new Set(groups.keys());
    deduped = deduped.filter((task) => {
      const key = keyFor(task);
      if (!key || !absorbed.has(key)) return true;
      return groups.get(key) === task;
    });
  };

  collapseByKey((task) => task.ticketId);
  collapseByKey((task) => (task.fileId ? task.fileId : null));
  collapseByKey((task) => resolvedReviewIdentityKey(task, reviews));

  deduped = dropOrphanStaleTasks(deduped, reviews);

  deduped.sort((a, b) => taskUpdatedAtMs(b) - taskUpdatedAtMs(a));
  return deduped;
}

export function buildApprovalAssetsByVersionId(
  tasks: ParticipationTask[],
  reviews: ProjectReviewItem[],
): Record<string, ApprovalRequestAssetVisual> {
  const map: Record<string, ApprovalRequestAssetVisual> = {};
  const reviewByTicketId = new Map<string, ProjectReviewItem>();
  for (const review of reviews) {
    if (review.ticketId) {
      reviewByTicketId.set(review.ticketId, review);
    }
  }

  for (const task of tasks) {
    const review =
      findReviewForParticipationTask(task, reviews) ??
      (task.ticketId ? (reviewByTicketId.get(task.ticketId) ?? null) : null);
    const parsed = parseAssetLabel(task.assetLabel);
    map[task.versionId] = {
      name: review?.name ?? parsed.name ?? "Review asset",
      versionNumber: review?.versionNumber ?? parsed.versionNumber,
      thumbnailUrl: review?.thumbnailUrl ?? null,
    };
  }
  return map;
}
